import { useMemo, useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Skeleton } from '@/components/ui-kit/skeleton';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { tableColumns } from './aimodel-columns';
import { IModelInfo } from '@/modules/gpt-chats/types/aimodel.service.type';
import { DeleteModel } from '@/modules/gpt-chats/components/aimodels/modals/aimodel-deletemodel-modal/aimodel-deletemodel-modal';
import { ModelEditKeyModal } from '@/modules/gpt-chats/components/aimodels/modals/aimodel-editkey-modal/aimodel-editkey-modal';
import { useToast } from '@/hooks/use-toast';
import { useValidateModel } from '@/modules/gpt-chats/hooks/use-aimodel';
import { CustomModelEditKeyModal } from '@/modules/gpt-chats/components/aimodels/modals/aimodel-editkey-modal-custom';

type AIModelsTableProps = {
  custom: boolean;
  models: IModelInfo[];
  isLoading: boolean;
};

const LoadingSkeleton = () => (
  <div className="grid w-full gap-2">
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} className="h-12 w-full rounded-xl" />
    ))}
  </div>
);

import { useTranslation } from 'react-i18next';

export const AIModelsTable = ({ custom, models, isLoading }: AIModelsTableProps) => {
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<IModelInfo | null>(null);
  const [editTarget, setEditTarget] = useState<IModelInfo | null>(null);
  const [editKeyModalOpen, setEditKeyModalOpen] = useState(false);
  const { toast } = useToast();

  const { mutate: validateModel, isPending: isValidating } = useValidateModel();

  const [actionMenuRowId, setActionMenuRowId] = useState<string | null>(null);

  const handleDeleteClick = useCallback((model: IModelInfo) => {
    setDeleteTarget(model);
  }, []);

  const handleEditClick = useCallback((model: IModelInfo) => {
    setEditTarget(model);
    setEditKeyModalOpen(true);
  }, []);

  const [validatingRowId, setValidatingRowId] = useState<string | null>(null);

  const handleValidateClick = useCallback(
    (model: IModelInfo) => {
      setValidatingRowId(model._id);

      validateModel(
        { modelId: model._id },
        {
          onSuccess: (res) => {
            const isValid = !!res.valid?.valid;
            const messageFromBackend =
              res.valid?.message || res.message || t('VALIDATION_COMPLETED');

            if (isValid) toast({ description: messageFromBackend, variant: 'success' });
            else
              toast({
                title: t('ERROR'),
                description: messageFromBackend,
                variant: 'destructive',
              });

            setValidatingRowId(null);
          },
          onError: (error) => {
            const fallbackMessage =
              error instanceof Error ? error.message : t('FAILED_TO_VALIDATE_MODEL');
            toast.error({ errors: fallbackMessage });

            setValidatingRowId(null);
          },
        }
      );
    },
    [validateModel, t, toast]
  );

  const columns = useMemo<ColumnDef<IModelInfo>[]>(
    () =>
      tableColumns(custom, {
        onEdit: handleEditClick,
        onDelete: handleDeleteClick,
        onValidate: handleValidateClick,
        isValidating,
        validatingRowId,
        onRowMenuOpenChange: (rowId, open) => {
          setActionMenuRowId(open ? rowId : null);
        },
        t,
      }),
    [
      custom,
      handleEditClick,
      handleDeleteClick,
      handleValidateClick,
      isValidating,
      validatingRowId,
      setActionMenuRowId,
      t,
    ]
  );

  const table = useReactTable({
    data: models,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) return <LoadingSkeleton />;

  const getClass = (columnId: string): string => {
    if (columnId === 'actions' || columnId === 'validate') return 'w-[10%]';
    return 'w-[20%]';
  };

  return (
    <div>
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow>
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <TableHead key={header.id} className={`${getClass(header.column.id)} truncate`}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))
            )}
          </TableRow>
        </TableHeader>

        <TableBody>
          {!models.length ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                {t('NO_MODELS_FOUND')}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={`group cursor-pointer ${
                  actionMenuRowId === row.original._id ? 'bg-muted' : ''
                }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={`${getClass(cell.column.id)} truncate`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {deleteTarget && (
        <DeleteModel
          modelId={deleteTarget._id}
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
        />
      )}

      {editTarget &&
        (custom ? (
          <CustomModelEditKeyModal
            editKeyModalOpen={editKeyModalOpen}
            setEditKeyModalOpen={(open) => {
              setEditKeyModalOpen(open);
              if (!open) setEditTarget(null);
            }}
            model={editTarget}
          />
        ) : (
          <ModelEditKeyModal
            modelOptions={[
              { model: editTarget.ModelName || '', goodName: editTarget.DisplayName || '' },
            ]}
            editKeyModalOpen={editKeyModalOpen}
            setEditKeyModalOpen={(open) => {
              setEditKeyModalOpen(open);
              if (!open) setEditTarget(null);
            }}
            model={editTarget}
          />
        ))}
    </div>
  );
};
